import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Check, MapPin, Clock, CreditCard, Banknote, Building2, Tag, ChevronRight, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Badge } from '../components/ui/badge';
import { useCart } from '../context/CartContext';
import { locations } from '../data/menuData';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const timeSlots = [
  'Lo antes posible',
  '12:00 - 12:30',
  '12:30 - 13:00',
  '13:00 - 13:30',
  '13:30 - 14:00',
  '14:00 - 14:30',
  '14:30 - 15:00',
  '19:00 - 19:30',
  '19:30 - 20:00',
  '20:00 - 20:30',
];

const paymentMethods = [
  { id: 'card', name: 'Tarjeta', icon: CreditCard, description: 'Débito o crédito' },
  { id: 'cash', name: 'Efectivo', icon: Banknote, description: 'Pago al recibir' },
  { id: 'transfer', name: 'Transferencia', icon: Building2, description: 'SPEI inmediato' },
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { 
    items, 
    selectedLocation, 
    setLocation,
    subtotal, 
    deliveryFee, 
    tip, 
    total,
    discount,
    applyPromo,
    removePromo,
    promoCode,
    clearCart,
  } = useCart();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    instructions: '',
    deliveryType: 'delivery',
    address: '',
    timeSlot: 'Lo antes posible',
    paymentMethod: 'card',
    promoCode: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if cart is empty
  if (items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-medium">Tu carrito está vacío</h2>
          <p className="text-muted-foreground mt-2">Agrega productos antes de continuar</p>
          <Link to="/menu">
            <Button className="mt-6 rounded-full">Ver menú</Button>
          </Link>
        </div>
      </div>
    );
  }

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'Nombre requerido';
      if (!formData.phone.trim()) newErrors.phone = 'Teléfono requerido';
      else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
        newErrors.phone = 'Ingresa 10 dígitos';
      }
    }
    
    if (step === 2) {
      if (!selectedLocation) newErrors.location = 'Selecciona una sucursal';
      if (formData.deliveryType === 'delivery' && !formData.address.trim()) {
        newErrors.address = 'Dirección requerida';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleApplyPromo = () => {
    const code = formData.promoCode.toUpperCase();
    if (code === 'BIENVENIDO20') {
      applyPromo(code, 20);
      toast.success('¡Cupón aplicado!', { description: '20% de descuento' });
    } else if (code === 'ENVIOGRATIS') {
      toast.success('¡Envío gratis aplicado!');
    } else {
      toast.error('Cupón inválido');
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Generate order ID
    const orderId = `LUM-${Date.now().toString(36).toUpperCase()}`;
    
    // Clear cart and navigate to order status
    clearCart();
    navigate(`/pedido/${orderId}`, { 
      state: { 
        orderData: {
          id: orderId,
          items,
          total,
          location: selectedLocation,
          deliveryType: formData.deliveryType,
          address: formData.address,
          timeSlot: formData.timeSlot,
          paymentMethod: formData.paymentMethod,
          customerName: formData.name,
          customerPhone: formData.phone,
        }
      }
    });
  };

  const steps = [
    { number: 1, title: 'Datos', icon: '👤' },
    { number: 2, title: 'Entrega', icon: '📍' },
    { number: 3, title: 'Pago', icon: '💳' },
  ];

  return (
    <div className="min-h-screen pb-24 md:pb-12 bg-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => currentStep > 1 ? prevStep() : navigate(-1)}
              className="p-2 hover:bg-secondary rounded-full transition-colors"
              data-testid="checkout-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="font-heading text-xl font-medium">Checkout</h1>
          </div>
          
          {/* Progress steps */}
          <div className="flex items-center justify-between mt-4">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                      currentStep >= step.number 
                        ? 'bg-accent text-accent-foreground' 
                        : 'bg-secondary text-muted-foreground'
                    }`}
                  >
                    {currentStep > step.number ? <Check className="w-5 h-5" /> : step.number}
                  </div>
                  <span className={`text-xs mt-1 ${currentStep >= step.number ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-2 transition-colors ${
                    currentStep > step.number ? 'bg-accent' : 'bg-secondary'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Form */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              {/* Step 1: Customer Data */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="font-heading text-2xl font-medium">Tus datos</h2>
                    <p className="text-muted-foreground mt-1">Para confirmar tu pedido</p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Nombre completo *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Juan Pérez"
                        className={`mt-1.5 h-12 ${errors.name ? 'border-destructive' : ''}`}
                        data-testid="checkout-name-input"
                      />
                      {errors.name && <p className="text-destructive text-sm mt-1">{errors.name}</p>}
                    </div>

                    <div>
                      <Label htmlFor="phone">Teléfono *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="81 1234 5678"
                        className={`mt-1.5 h-12 ${errors.phone ? 'border-destructive' : ''}`}
                        data-testid="checkout-phone-input"
                      />
                      {errors.phone && <p className="text-destructive text-sm mt-1">{errors.phone}</p>}
                    </div>

                    <div>
                      <Label htmlFor="instructions">Instrucciones especiales</Label>
                      <Textarea
                        id="instructions"
                        value={formData.instructions}
                        onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                        placeholder="Ej: Tocar el timbre, dejar en recepción..."
                        className="mt-1.5 min-h-[80px] resize-none"
                        data-testid="checkout-instructions-input"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Delivery */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="font-heading text-2xl font-medium">Entrega o retiro</h2>
                    <p className="text-muted-foreground mt-1">¿Cómo quieres recibir tu pedido?</p>
                  </div>

                  {/* Delivery type */}
                  <RadioGroup
                    value={formData.deliveryType}
                    onValueChange={(value) => setFormData({ ...formData, deliveryType: value })}
                    className="grid grid-cols-2 gap-4"
                  >
                    <label 
                      className={`p-4 rounded-xl border cursor-pointer transition-all ${
                        formData.deliveryType === 'delivery' 
                          ? 'border-accent bg-accent/5' 
                          : 'border-border hover:border-accent/50'
                      }`}
                      data-testid="delivery-option"
                    >
                      <RadioGroupItem value="delivery" className="sr-only" />
                      <div className="text-center">
                        <span className="text-2xl">🚴</span>
                        <p className="font-medium mt-2">A domicilio</p>
                        <p className="text-xs text-muted-foreground">
                          {deliveryFee > 0 ? `$${deliveryFee} envío` : 'Envío gratis'}
                        </p>
                      </div>
                    </label>
                    <label 
                      className={`p-4 rounded-xl border cursor-pointer transition-all ${
                        formData.deliveryType === 'pickup' 
                          ? 'border-accent bg-accent/5' 
                          : 'border-border hover:border-accent/50'
                      }`}
                      data-testid="pickup-option"
                    >
                      <RadioGroupItem value="pickup" className="sr-only" />
                      <div className="text-center">
                        <span className="text-2xl">🏪</span>
                        <p className="font-medium mt-2">Recoger</p>
                        <p className="text-xs text-muted-foreground">Sin costo</p>
                      </div>
                    </label>
                  </RadioGroup>

                  {/* Location selector */}
                  <div>
                    <Label>Sucursal *</Label>
                    {errors.location && (
                      <p className="text-destructive text-sm flex items-center gap-1 mt-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.location}
                      </p>
                    )}
                    <div className="mt-2 space-y-2 max-h-[200px] overflow-y-auto">
                      {locations.map(loc => (
                        <button
                          key={loc.id}
                          onClick={() => setLocation(loc)}
                          className={`w-full text-left p-3 rounded-xl border transition-all ${
                            selectedLocation?.id === loc.id
                              ? 'border-accent bg-accent/5'
                              : 'border-border hover:border-accent/50'
                          }`}
                          data-testid={`checkout-location-${loc.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-accent" />
                              <span className="font-medium text-sm">{loc.name}</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              {loc.avgPrepTime} min
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Address for delivery */}
                  {formData.deliveryType === 'delivery' && (
                    <div>
                      <Label htmlFor="address">Dirección de entrega *</Label>
                      <Textarea
                        id="address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Calle, número, colonia, referencias..."
                        className={`mt-1.5 min-h-[80px] resize-none ${errors.address ? 'border-destructive' : ''}`}
                        data-testid="checkout-address-input"
                      />
                      {errors.address && <p className="text-destructive text-sm mt-1">{errors.address}</p>}
                    </div>
                  )}

                  {/* Time slot */}
                  <div>
                    <Label>Horario</Label>
                    <select
                      value={formData.timeSlot}
                      onChange={(e) => setFormData({ ...formData, timeSlot: e.target.value })}
                      className="w-full mt-1.5 h-12 px-3 bg-background border border-input rounded-lg"
                      data-testid="checkout-time-select"
                    >
                      {timeSlots.map(slot => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Payment */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="font-heading text-2xl font-medium">Método de pago</h2>
                    <p className="text-muted-foreground mt-1">Selecciona cómo quieres pagar</p>
                  </div>

                  <RadioGroup
                    value={formData.paymentMethod}
                    onValueChange={(value) => setFormData({ ...formData, paymentMethod: value })}
                    className="space-y-3"
                  >
                    {paymentMethods.map(method => (
                      <label
                        key={method.id}
                        className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                          formData.paymentMethod === method.id
                            ? 'border-accent bg-accent/5'
                            : 'border-border hover:border-accent/50'
                        }`}
                        data-testid={`payment-${method.id}`}
                      >
                        <RadioGroupItem value={method.id} className="sr-only" />
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          formData.paymentMethod === method.id ? 'bg-accent text-accent-foreground' : 'bg-secondary'
                        }`}>
                          <method.icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{method.name}</p>
                          <p className="text-sm text-muted-foreground">{method.description}</p>
                        </div>
                        {formData.paymentMethod === method.id && (
                          <Check className="w-5 h-5 text-accent" />
                        )}
                      </label>
                    ))}
                  </RadioGroup>

                  {/* Promo code */}
                  <div>
                    <Label>Código promocional</Label>
                    <div className="flex gap-2 mt-1.5">
                      <Input
                        value={formData.promoCode}
                        onChange={(e) => setFormData({ ...formData, promoCode: e.target.value })}
                        placeholder="Ingresa tu código"
                        className="flex-1 h-12"
                        disabled={!!promoCode}
                        data-testid="checkout-promo-input"
                      />
                      {promoCode ? (
                        <Button 
                          variant="outline" 
                          onClick={removePromo}
                          className="h-12"
                        >
                          Quitar
                        </Button>
                      ) : (
                        <Button 
                          variant="outline" 
                          onClick={handleApplyPromo}
                          className="h-12"
                          data-testid="apply-promo-btn"
                        >
                          <Tag className="w-4 h-4 mr-1" />
                          Aplicar
                        </Button>
                      )}
                    </div>
                    {promoCode && (
                      <Badge className="mt-2 bg-accent/10 text-accent">
                        <Check className="w-3 h-3 mr-1" />
                        {promoCode} aplicado
                      </Badge>
                    )}
                  </div>

                  {/* Simulated payment notice */}
                  <div className="p-4 bg-secondary/50 rounded-xl">
                    <p className="text-sm text-muted-foreground text-center">
                      🔒 Esta es una demo. No se procesará ningún pago real.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-2">
            <div className="sticky top-32 bg-card border border-border/50 rounded-2xl p-6">
              <h3 className="font-medium mb-4">Resumen del pedido</h3>
              
              <div className="space-y-3 max-h-[200px] overflow-y-auto">
                {items.map(item => (
                  <div key={item.cartId} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {item.quantity}x {item.name}
                    </span>
                    <span>${(item.price * item.quantity).toFixed(0)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-border/50 mt-4 pt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>${subtotal.toFixed(0)}</span>
                </div>
                {formData.deliveryType === 'delivery' && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Envío</span>
                    <span className={deliveryFee === 0 ? 'text-accent' : ''}>
                      {deliveryFee === 0 ? 'Gratis' : `$${deliveryFee}`}
                    </span>
                  </div>
                )}
                {tip > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Propina</span>
                    <span>${tip.toFixed(0)}</span>
                  </div>
                )}
                {discount > 0 && (
                  <div className="flex justify-between text-accent">
                    <span>Descuento</span>
                    <span>-${discount.toFixed(0)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-border/50 text-lg font-semibold">
                  <span>Total</span>
                  <span>${(formData.deliveryType === 'pickup' ? total - deliveryFee : total).toFixed(0)}</span>
                </div>
              </div>

              {/* Action button */}
              <Button
                onClick={currentStep < 3 ? nextStep : handleSubmit}
                disabled={isSubmitting}
                className="w-full mt-6 h-12 rounded-full font-medium"
                data-testid="checkout-continue-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Procesando...
                  </span>
                ) : currentStep < 3 ? (
                  <>
                    Continuar
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </>
                ) : (
                  <>
                    Confirmar pedido · ${(formData.deliveryType === 'pickup' ? total - deliveryFee : total).toFixed(0)}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
